import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { FeedbackPriority } from "@/lib/feedback-priority";
import { invalidateFor } from "@/lib/cache-registry";
import { toast } from "sonner";

export type FeedbackStatus = "new" | "acknowledged" | "closed" | "deleted";

export interface FeedbackReply {
  id: string;
  body: string;
  created_at: string;
  author: { full_name: string } | null;
}

export interface FeedbackRow {
  id: string;
  user_id: string | null;
  type: "bug" | "feature";
  /** VIB-2085: NULL på rader sendt inn før feltet fantes. */
  priority: FeedbackPriority | null;
  body: string;
  page_url: string | null;
  created_at: string;
  status: FeedbackStatus;
  linear_issue_id: string | null;
  handled_at: string | null;
  handled_by: string | null;
  user: { full_name: string; avatar_url: string | null } | null;
  replies: FeedbackReply[];
  attachment_paths: string[];
}

export interface FeedbackFilters {
  type?: "bug" | "feature" | "all";
  status?: FeedbackStatus | "all" | "active";
  /**
   * VIB-2085. "unset" finner radene som mangler prioritet — enten sendt inn før
   * feltet fantes, eller via en vei som ikke satte det.
   */
  priority?: FeedbackPriority | "all" | "unset";
}

interface AdminFeedbackRaw {
  id: string;
  user_id: string | null;
  type: "bug" | "feature";
  priority: FeedbackPriority | null;
  body: string;
  page_url: string | null;
  created_at: string;
  status: FeedbackStatus;
  linear_issue_id: string | null;
  handled_at: string | null;
  handled_by: string | null;
  users: { full_name: string; avatar_url: string | null } | null;
  feedback_replies: FeedbackReply[] | null;
  attachment_paths: string[] | null;
}

/**
 * Liste over feedback for admin/superadmin. Default visning ekskluderer
 * deleted-rader; "active" filter ekskluderer også closed.
 */
export function useAdminFeedback(filters: FeedbackFilters = {}) {
  return useQuery({
    queryKey: ["admin-feedback", filters],
    queryFn: async (): Promise<FeedbackRow[]> => {
      let query = supabase
        .from("feedback")
        .select("id, user_id, type, priority, body, page_url, created_at, status, linear_issue_id, handled_at, handled_by, attachment_paths, users:user_id(full_name, avatar_url), feedback_replies(id, body, created_at, author:author_id(full_name))")
        .order("created_at", { ascending: false });

      if (filters.type && filters.type !== "all") {
        query = query.eq("type", filters.type);
      }
      if (filters.priority === "unset") {
        query = query.is("priority", null);
      } else if (filters.priority && filters.priority !== "all") {
        query = query.eq("priority", filters.priority);
      }
      if (filters.status === "active") {
        // Løst (acknowledged) og Avsluttet (closed) er terminale → kun Ny er aktiv.
        query = query.eq("status", "new");
      } else if (filters.status && filters.status !== "all") {
        query = query.eq("status", filters.status);
      } else {
        query = query.neq("status", "deleted");
      }

      const { data, error } = (await (query as unknown as Promise<{
        data: AdminFeedbackRaw[] | null;
        error: { message: string } | null;
      }>));
      if (error) throw new Error(error.message);
      return (data ?? []).map((r) => ({
        id: r.id,
        user_id: r.user_id,
        type: r.type,
        priority: r.priority ?? null,
        body: r.body,
        page_url: r.page_url,
        created_at: r.created_at,
        status: r.status,
        linear_issue_id: r.linear_issue_id,
        handled_at: r.handled_at,
        handled_by: r.handled_by,
        user: r.users ?? null,
        replies: (r.feedback_replies ?? [])
          .slice()
          .sort((a, b) => a.created_at.localeCompare(b.created_at)),
        attachment_paths: r.attachment_paths ?? [],
      }));
    },
    staleTime: 30 * 1000,
  });
}

/**
 * Antall aktive (status='new') feedback — brukes for sidebar-badge på
 * admin-bruker. Løst (acknowledged) og Avsluttet (closed) er terminale.
 */
export function useActiveFeedbackCount(enabled = true) {
  return useQuery({
    queryKey: ["admin-feedback-count"],
    enabled,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from("feedback")
        .select("id", { count: "exact", head: true })
        .eq("status", "new");
      if (error) throw new Error(error.message);
      return count ?? 0;
    },
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

interface UpdateStatusInput {
  id: string;
  status: FeedbackStatus;
}

interface UpdateStatusResponse {
  ok: boolean;
  slack_ok: boolean;
  slack_error: string | null;
}

/**
 * VIB-782: Endrer feedback-status via `update-feedback-status` edge function
 * i stedet for direkte Supabase-update. Edge function-en gjør DB-skrivingen
 * OG kaller Slack chat.update så badge-en i #solvrev-feedback reflekterer ny
 * status. Handled_by settes til den faktisk innloggede brukeren (caller) på
 * serveren — vi trenger ikke sende den fra klienten.
 */
export function useUpdateFeedbackStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: UpdateStatusInput) => {
      const { data, error } = await supabase.functions.invoke<UpdateStatusResponse>(
        "update-feedback-status",
        { body: { feedback_id: id, status } },
      );
      if (error) throw error;
      if (!data?.ok) throw new Error("Status-oppdatering feilet");
      return data;
    },
    onSuccess: async (data) => {
      await invalidateFor(qc, "feedback");
      if (data?.slack_error) {
        // DB-en er oppdatert, men Slack-synkroniseringen feilet. Vis warning
        // så Erik kan rette opp env-vars / bot-token om nødvendig.
        toast.warning("Status oppdatert, Slack-sync feilet", {
          description: data.slack_error,
        });
      }
    },
    onError: (err) => {
      toast.error(`Kunne ikke oppdatere status: ${(err as Error).message}`);
    },
  });
}

interface AddReplyInput {
  feedbackId: string;
  body: string;
}

/**
 * Sender et admin→melder-svar via `add-feedback-reply` edge function.
 * feedback_replies skrives kun av service_role, så insertet MÅ gå via edge-fn.
 * Svaret utløser e-post til melder (VIB-1239) og vises i App Home +
 * /mine-tilbakemeldinger. Author settes til faktisk innlogget admin på server.
 */
export function useAddFeedbackReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ feedbackId, body }: AddReplyInput) => {
      const { data, error } = await supabase.functions.invoke<{ ok: boolean }>(
        "add-feedback-reply",
        { body: { feedback_id: feedbackId, body } },
      );
      if (error) throw error;
      if (!data?.ok) throw new Error("Kunne ikke sende svar");
      return data;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "feedback");
      toast.success("Svar sendt til melder");
    },
    onError: (err) => {
      toast.error(`Kunne ikke sende svar: ${(err as Error).message}`);
    },
  });
}

export function useSoftDeleteFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.functions.invoke<UpdateStatusResponse>(
        "update-feedback-status",
        { body: { feedback_id: id, status: "deleted" } },
      );
      if (error) throw error;
      if (!data?.ok) throw new Error("Sletting feilet");
      return data;
    },
    onSuccess: async (data) => {
      await invalidateFor(qc, "feedback");
      toast.success("Feedback slettet");
      if (data?.slack_error) {
        toast.warning("Slett'et i DB, Slack-sync feilet", {
          description: data.slack_error,
        });
      }
    },
    onError: (err) => {
      toast.error(`Kunne ikke slette: ${(err as Error).message}`);
    },
  });
}
